import React, { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOnboardingOptional } from './OnboardingContext';
import { ONBOARDING_STEPS, getStepMeta } from './onboardingCopy';
import styles from './OnboardingRail.module.scss';

const TRACKED = ONBOARDING_STEPS.filter(
  s => s.id !== 'done' && s.id !== 'intro'
);

const OnboardingRail: React.FC = () => {
  const onboarding = useOnboardingOptional();
  const navigate = useNavigate();
  const location = useLocation();
  const [localBusy, setLocalBusy] = useState(false);

  if (!onboarding?.showTour) return null;

  const {
    currentStepId,
    currentMeta,
    steps,
    start,
    goNext,
    dismiss,
    requestOpenApprove,
    requestOpenCreateOrder,
    stepFlashKey,
    actionBusy,
    isReplay,
    currentSatisfied,
    routeForStep,
  } = onboarding;
  const meta = currentMeta ?? getStepMeta(currentStepId);
  const isApprove = currentStepId === 'approve';
  const isOrder = currentStepId === 'order';
  const busy = actionBusy || localBusy;
  // If org data already covers this step, never trap the user on a no-op CTA
  const canSkip = Boolean(currentSatisfied);
  const targetRoute = meta.route;
  const alreadyOnPage =
    location.pathname === targetRoute ||
    location.pathname.startsWith(`${targetRoute}/`);

  const finishBusy = () => setLocalBusy(false);

  const advanceAndGo = () => {
    void goNext().then(res => {
      if (!res) {
        finishBusy();
        return;
      }
      const next = res.derivedCurrentStepId ?? nextGuideFallback(currentStepId);
      const dest = routeForStep(next);
      if (next === 'approve') {
        requestOpenApprove();
      }
      if (
        location.pathname !== dest &&
        !location.pathname.startsWith(`${dest}/`)
      ) {
        navigate(dest);
      }
      finishBusy();
    });
  };

  const handlePrimary = () => {
    if (busy) return;
    setLocalBusy(true);

    if (currentStepId === 'intro') {
      void start().then(() => {
        navigate('/inventory');
        finishBusy();
      });
      return;
    }

    // Already done for this step → always advance (replay or first-run)
    if (canSkip) {
      advanceAndGo();
      return;
    }

    if (isApprove) {
      requestOpenApprove();
      if (!alreadyOnPage) navigate('/orders');
      finishBusy();
      return;
    }

    if (isOrder) {
      // Already on Ventas: open the create form instead of a dead navigate
      requestOpenCreateOrder();
      if (!alreadyOnPage) navigate('/orders');
      finishBusy();
      return;
    }

    if (alreadyOnPage) {
      // Same page, nothing to create yet — still give feedback by advancing if possible
      finishBusy();
      return;
    }

    navigate(targetRoute);
    finishBusy();
  };

  const ctaLabel =
    currentStepId === 'intro'
      ? meta.cta
      : canSkip
        ? 'Siguiente'
        : meta.cta;

  return (
    <aside
      key={stepFlashKey}
      className={`${styles.rail} ${isApprove && !canSkip ? styles.railUrgent : ''}`}
      aria-label="Progreso de configuración"
      aria-busy={busy}
    >
      <p className={styles.label}>
        {isReplay ? 'Repaso guiado' : 'Tu primera venta'}
      </p>
      <div className={styles.dots} aria-hidden>
        {TRACKED.map(step => {
          const done = steps.find(s => s.id === step.id)?.done;
          const current = currentStepId === step.id;
          const className = [
            styles.dot,
            done ? styles.dotDone : '',
            current ? styles.dotCurrent : '',
          ]
            .filter(Boolean)
            .join(' ');
          return <span key={step.id} className={className} />;
        })}
      </div>
      <h3 className={styles.title}>{meta.title}</h3>
      <p className={styles.why}>
        {canSkip
          ? 'Ya tienes este paso listo. Continúa al siguiente.'
          : meta.why}
      </p>
      <div className={styles.actions}>
        <Button
          size="sm"
          variant={isApprove && !canSkip ? 'warning' : 'primary'}
          className={isApprove && !canSkip ? styles.ctaPulse : undefined}
          disabled={busy}
          onClick={handlePrimary}
        >
          {busy ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              …
            </>
          ) : (
            ctaLabel
          )}
        </Button>
        <button
          type="button"
          className={styles.later}
          disabled={busy}
          onClick={() => void dismiss()}
        >
          Más tarde
        </button>
      </div>
    </aside>
  );
};

function nextGuideFallback(
  id: string | null
): 'intro' | 'product' | 'client' | 'order' | 'approve' | 'done' {
  const order = ['intro', 'product', 'client', 'order', 'approve', 'done'] as const;
  const idx = order.indexOf((id as (typeof order)[number]) || 'intro');
  if (idx < 0 || idx >= order.length - 1) return 'done';
  return order[idx + 1];
}

export default OnboardingRail;
